import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Loader } from "semantic-ui-react";
import { submitInvalidQuestionnaire as submitInvalidQuestionnaireAction } from "@teamprevent/common/questionnaires/actions";

import {
    HealthSurveillanceQuestionnaire, HealthSurveillanceQuestionnaireKey
} from "@teamprevent/common/healthSurveillance";

import { AnsweredQuestionWithId, FileAnswer } from "@teamprevent/common/questionnaires/model";

import { ApplicationState } from "../../applicationState";
import {
    getHealthSurveillanceQuestionnaireEvidenceApi,
    getOhCaseDocumentDownloadLink
} from "../healthSurveillanceApi";

import { useClinicAndAppointment } from "../../calendar/clinics/hooks";
import {
    deleteEmployeeQuestionnaireEvidence, loadHealthSurveillanceQuestionnaireForClinicFromServer, submitHealthSurveillanceQuestionnaire
} from "../actions";
import { OrgStructureDetailModel, loadOrgStructures } from "../../crud/orgStructure";
import { useEmployeeRecordDetailsModel } from "../../crud/employeeRecord/hooks";
import { useMedicalTypesForAppointment } from "../hooks";
import { useCustomMedicalTypes } from "../../customMedicalTypes/hooks/useCustomMedicalTypes";

import { push } from "redux-little-router";

export const ClinicHealthSurveillanceEmployeeQuestionnaire = () => {
    const dispatch = useDispatch();
    const date = useSelector((state: ApplicationState) => state.router.params!.date);
    const clinicId = useSelector((state: ApplicationState) => state.router.params!.clinicId!);
    const appointmentId = useSelector((state: ApplicationState) => state.router.params!.appointmentId!);
    const navigateToNextPage = React.useCallback(() => dispatch(push(`/clinics/calendar/${date}/${clinicId}`)), [clinicId, date, dispatch]);
    const { appointment, isLoaded: clinicLoaded } = useClinicAndAppointment({ clinicId, appointmentId });
    const fileDownload = React.useCallback((fileAnswer: FileAnswer): string => {
        return getOhCaseDocumentDownloadLink(appointment!.ohCaseId, fileAnswer);
    }, [appointment]);
    const submitQuestionnaire = React.useCallback(
        (questions: AnsweredQuestionWithId[]) => dispatch(submitHealthSurveillanceQuestionnaire(
            clinicId,
            appointmentId,
            questions,
            HealthSurveillanceQuestionnaireKey
        )), [appointmentId, clinicId, dispatch]);
    const submitInvalidQuestionnaire = React.useCallback(() => dispatch(submitInvalidQuestionnaireAction(HealthSurveillanceQuestionnaireKey)), [dispatch]);
    const getOrgLevels = React.useCallback(
        async () => (await dispatch(loadOrgStructures({ customerId: appointment!.customer!.id }))).map((o: OrgStructureDetailModel) =>
            ({ id: o.id, name: o.name })),
        [dispatch, appointment]);

    const {
        employeeRecord,
        isLoaded: employeeLoaded
    } = useEmployeeRecordDetailsModel({ customerId: appointment?.customer!.id ?? "", employeeRecordId: appointment?.employee!.id ?? "" });
    const onDeleteEvidence = React.useCallback(
        (_fileId: string, _fileName: string, ohCaseDocumentId: string) => {
            return dispatch(deleteEmployeeQuestionnaireEvidence(
                appointment!.ohCaseId!, appointment!.customer!.id, ohCaseDocumentId
            ));
        },
        [appointment, dispatch]);
    const {
        medicalTypes: medicalTypesForQuestionnaire,
        refetch: loadMedicalTypesForQuestionnaire
    } = useMedicalTypesForAppointment({ appointmentId, clinicId });
    const {
        customMedicalTypes,
        refetch: loadCustomMedicalTypes
    } = useCustomMedicalTypes({ enable: false });
    const getQuestionnaire = React.useCallback(
        () => dispatch(loadHealthSurveillanceQuestionnaireForClinicFromServer(clinicId, appointmentId)),
        [appointmentId, clinicId, dispatch]
    );

    if (employeeLoaded === false || clinicLoaded === false) {
        return <Loader active />;
    }

    const fileUploadApi = getHealthSurveillanceQuestionnaireEvidenceApi(appointment!.ohCaseId!, appointment!.customer!.id);
    return (<HealthSurveillanceQuestionnaire
        task={undefined}
        fileUploadApi={fileUploadApi}
        getDownloadLink={fileDownload}
        autoFill
        submitQuestionnaire={submitQuestionnaire}
        submitInvalidQuestionnaire={submitInvalidQuestionnaire}
        navigateToNextPage={navigateToNextPage}
        getOrgLevels={getOrgLevels}
        recordMedicalsSeparately={true}
        employeeRecord={employeeRecord}
        onDeleteFile={onDeleteEvidence}
        loadMedicalTypesForQuestionnaire={loadMedicalTypesForQuestionnaire}
        getQuestionnaire={getQuestionnaire}
        loadCustomMedicalTypes={loadCustomMedicalTypes}
        medicalTypesForQuestionnaire={medicalTypesForQuestionnaire}
        customMedicalTypes={customMedicalTypes}
    />);
};
